<template>
  <div class="ord-root" :data-sidebar="darkMode ? 'dark' : 'light'">
    <div class="ord-topbar">
      <div class="container d-flex align-items-center gap-3">
        <a href="javascript:void(0)" @click="$router.push('/')"><img src="/assets/images/flockr_logo.png" alt="Flockr" style="height:32px;" /></a>
        <button class="btn btn-sm btn-link text-muted p-0" @click="$router.push('/orders')"><i class="mdi mdi-arrow-left me-1"></i>Orders</button>
      </div>
    </div>
    <div class="container ord-container">
      <div v-if="loading" class="text-center py-5"><div class="spinner-border text-warning"></div></div>
      <div v-else-if="loadError" class="text-center py-5">
        <i class="mdi mdi-cloud-off-outline" style="font-size:48px;color:#FF6B6B;"></i>
        <p class="mt-2" style="color:var(--fm-h,#F5F0E8);font-weight:600;">{{ loadError }}</p>
        <div class="mt-3 d-flex gap-2 justify-content-center">
          <button class="btn btn-sm btn-warning" @click="load"><i class="mdi mdi-refresh me-1"></i>Retry</button>
          <button class="btn btn-sm btn-outline-secondary" @click="$router.push('/orders')"><i class="mdi mdi-arrow-left me-1"></i>Back to Orders</button>
        </div>
      </div>
      <div v-else-if="!order.id" class="text-center py-5"><i class="mdi mdi-alert-circle" style="font-size:48px;color:#FF6B6B;"></i><p class="mt-2" style="color:var(--fm-s,#777);">Order not found</p></div>
      <div v-else class="row g-4">
        <div class="col-lg-7">
          <div class="p2c mb-3">
            <div class="d-flex align-items-center justify-content-between mb-3">
              <div><span style="font-size:13px;color:var(--fm-s,#777);font-family:monospace;">#{{ order.order_number }}</span>
                <span class="p2b ms-2" :class="'st-'+order.status">{{ (order.status||'').replace(/_/g,' ') }}</span></div>
              <span style="color:var(--fm-s,#777);font-size:12px;">{{ fmtD(order.created_at) }}</span>
            </div>
            <div class="d-flex align-items-center gap-3">
              <div class="p2t"><img v-if="order.listing_photo" :src="pu(order.listing_photo)" /><i v-else class="mdi mdi-paw" style="font-size:28px;color:#BFA164;"></i></div>
              <div class="flex-fill"><div style="font-size:15px;font-weight:600;">{{ order.listing_title }}</div><div class="p2s">Qty: {{ order.quantity }}</div></div>
              <div style="font-size:18px;font-weight:700;color:#BFA164;">KES {{ fN(order.total_amount) }}</div>
            </div>
          </div>
          <div class="p2c mb-3">
            <h6 class="p2h"><i class="mdi mdi-timeline me-2"></i>Timeline</h6>
            <div style="padding-left:20px;">
              <div v-for="(s,i) in steps" :key="i" class="tli" :class="{done:isAf(s.k)}"><div class="tld"></div><div><div style="font-size:13px;font-weight:600;color:var(--fm-h,#F5F0E8);">{{ s.l }}</div></div></div>
            </div>
          </div>
          <div class="p2c mb-3" v-if="order.transport_option">
            <h6 class="p2h"><i class="mdi mdi-truck me-2"></i>Delivery</h6>
            <div class="p2s">{{ order.transport_option==='buyer_collects'?'Buyer Collects':'Seller Delivers' }}</div>
            <div v-if="order.delivery_location" class="p2s mt-1">Location: {{ order.delivery_location }}</div>
            <div v-if="order.estimated_arrival_at" class="p2s mt-1" style="color:#BFA164;font-weight:600;"><i class="mdi mdi-clock-outline me-1"></i>Estimated arrival: {{ fmtDate(order.estimated_arrival_at) }}</div>
          </div>

          <!-- Compliance Documents — Kenya sub-county vet office requires a Movement Permit and
               No Objection Certificate for cross-county livestock sales. Cattle/sheep/goat/pig only.
               Server also enforces this via updateOrderStatus so this UI can't be bypassed. -->
          <div class="p2c mb-3" v-if="needsCompliance && isAf('paid')">
            <h6 class="p2h"><i class="mdi mdi-file-certificate me-2"></i>Compliance Documents</h6>
            <div style="color:var(--fm-s,#6B6356);font-size:12px;margin:-4px 0 12px;">
              Required before the animal can be transported. Uploaded by the seller before Mark Shipped.
            </div>
            <div v-for="slot in complianceSlots" :key="slot.key" class="cmp-slot">
              <div class="d-flex align-items-center justify-content-between" style="gap:10px;">
                <div style="flex:1;min-width:0;">
                  <div style="font-size:13px;font-weight:600;color:var(--fm-h,#F5F0E8);">
                    {{ slot.label }}
                    <span v-if="slot.required" style="color:#FF6B6B;font-size:11px;font-weight:500;">*</span>
                    <span v-else style="color:var(--fm-s,#6B6356);font-size:10px;font-weight:400;">(recommended)</span>
                  </div>
                  <div v-if="docFor(slot.key)" style="font-size:11px;color:var(--fm-m,#A09888);margin-top:2px;">
                    <i class="mdi mdi-check-decagram" v-if="Number(docFor(slot.key).verified_by_admin)===1" style="color:#50C878;"></i>
                    <span v-if="Number(docFor(slot.key).verified_by_admin)===1" style="color:#50C878;font-weight:600;">Verified · </span>
                    <span v-else>Uploaded · </span>
                    Ref {{ docFor(slot.key).permit_ref_no || '—' }}
                    <span v-if="slot.key==='veterinary_health_cert' && docFor(slot.key).kvb_number"> · KVB {{ docFor(slot.key).kvb_number }}</span>
                    <span v-if="docFor(slot.key).valid_until"> · valid till {{ fmtD(docFor(slot.key).valid_until) }}</span>
                  </div>
                  <div v-else-if="slot.required" style="font-size:11px;color:#FF9800;margin-top:2px;"><i class="mdi mdi-alert-circle-outline me-1"></i>Not uploaded yet</div>
                  <div v-else style="font-size:11px;color:var(--fm-s,#6B6356);margin-top:2px;">Optional — attach if the vet has issued one.</div>
                </div>
                <div class="d-flex align-items-center gap-2">
                  <a v-if="docFor(slot.key)" :href="pu(docFor(slot.key).photo_url)" target="_blank" class="btn btn-sm btn-outline-secondary" title="View document"><i class="mdi mdi-eye"></i></a>
                  <button v-if="isSeller && !docFor(slot.key)" class="btn btn-sm btn-warning" @click="openComplianceUpload(slot)">
                    <i class="mdi mdi-upload me-1"></i>Upload
                  </button>
                  <button v-else-if="isSeller && docFor(slot.key) && Number(docFor(slot.key).verified_by_admin)===0" class="btn btn-sm btn-outline-warning" @click="openComplianceUpload(slot)" title="Replace">
                    <i class="mdi mdi-refresh"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-lg-5">
          <div class="p2c mb-3">
            <h6 class="p2h"><i class="mdi mdi-receipt me-2"></i>Payment</h6>
            <div class="p2l"><span>Subtotal</span><span>KES {{ fN(order.subtotal) }}</span></div>
            <!-- Client issue 27: the 3% fee comes out of the seller's payout, so only the seller sees it -->
            <div v-if="isSeller" class="p2l"><span>Platform Fee (deducted from payout)</span><span>KES {{ fN(order.platform_fee) }}</span></div>
            <div v-if="Number(order.transport_fee)>0" class="p2l"><span>Transport</span><span>KES {{ fN(order.transport_fee) }}</span></div>
            <div class="p2l" style="font-size:16px;font-weight:700;color:var(--fm-h,#1A1A1A);border-top:2px solid #BFA164;border-bottom:none;padding-top:10px;margin-top:4px;"><span>Total</span><span>KES {{ fN(order.total_amount) }}</span></div>
            <div v-if="order.escrow" style="display:flex;align-items:center;padding:8px 12px;background:rgba(80,200,120,.06);border:1px solid rgba(80,200,120,.2);border-radius:8px;color:#50C878;font-size:12px;margin-top:12px;"><i class="mdi mdi-shield-check me-2"></i>Escrow: {{ order.escrow.status }}</div>
          </div>
          <div class="p2c mb-3">
            <h6 class="p2h"><i class="mdi mdi-account-multiple me-2"></i>Parties</h6>
            <div class="p2s mb-2"><strong style="color:#BFA164;">Buyer:</strong> {{ order.buyer_name || 'Unknown' }}</div>
            <div class="p2s"><strong style="color:#BFA164;">Seller:</strong> {{ order.seller_name || 'Unknown' }}</div>
          </div>
          <div class="p2c mb-3">
            <h6 class="p2h"><i class="mdi mdi-gesture-tap me-2"></i>Actions</h6>

            <!-- CART GROUP CHECKOUT — shows for orders that are part of a multi-seller cart payment.
                 Replaces the per-order STK box so the buyer doesn't double-pay. -->
            <div v-if="order.order_group_id && (order.status==='pending_payment'||order.status==='payment_initiated'||order.status==='stk_sent') && isBuyer" class="od-pay-box mb-3">
              <div class="od-pay-header"><i class="mdi mdi-cart-outline me-1" style="color:#BFA164;"></i>Part of a Cart Checkout</div>
              <div style="font-size:12px;color:var(--fm-m,#A09888);margin:8px 0 12px;line-height:1.5;">
                This order was paid for as part of a multi-seller cart. <span v-if="order.siblings && order.siblings.length">There are {{ order.siblings.length }} other order{{ order.siblings.length===1?'':'s' }} in the same payment.</span>
                <span v-if="!groupPaymentCompleted"> Once the M-Pesa prompt is paid, all orders in the group flip to <strong style="color:#50C878;">paid</strong> at the same time.</span>
              </div>

              <!-- Group payment status indicator -->
              <div v-if="order.groupPayment" class="mt-2 mb-3" style="padding:10px;border-radius:8px;background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#1E1E1E);">
                <div style="font-size:11px;color:var(--fm-s,#6B6356);text-transform:uppercase;letter-spacing:.5px;">Cart payment status</div>
                <div class="d-flex align-items-center gap-2 mt-1">
                  <span v-if="order.groupPayment.status==='completed'" style="color:#50C878;font-weight:600;font-size:13px;"><i class="mdi mdi-check-circle me-1"></i>Paid · {{ (order.currency || 'KES') }} {{ formatPrice(order.groupPayment.amount) }}</span>
                  <span v-else-if="order.groupPayment.status==='stk_sent'||order.groupPayment.status==='initiated'" style="color:#FFAB00;font-weight:600;font-size:13px;"><i class="mdi mdi-clock-outline me-1"></i>Waiting for PIN · {{ (order.currency || 'KES') }} {{ formatPrice(order.groupPayment.amount) }}</span>
                  <span v-else-if="order.groupPayment.status==='cancelled'" style="color:#FF6B6B;font-weight:600;font-size:13px;"><i class="mdi mdi-close-circle me-1"></i>Cancelled</span>
                  <span v-else-if="order.groupPayment.status==='failed'" style="color:#FF6B6B;font-weight:600;font-size:13px;"><i class="mdi mdi-alert me-1"></i>Failed</span>
                  <span v-else style="color:var(--fm-m,#A09888);font-size:13px;text-transform:capitalize;">{{ order.groupPayment.status }}</span>
                </div>
                <div v-if="order.groupPayment.payer_phone" style="font-size:11px;color:var(--fm-s,#6B6356);margin-top:2px;">Sent to +{{ order.groupPayment.payer_phone }}</div>
              </div>

              <!-- Siblings list -->
              <div v-if="order.siblings && order.siblings.length" class="mt-2 mb-3">
                <div style="font-size:11px;color:var(--fm-s,#6B6356);text-transform:uppercase;letter-spacing:.5px;margin-bottom:6px;">Sibling orders in this cart</div>
                <div v-for="s in order.siblings" :key="s.id" style="display:flex;justify-content:space-between;align-items:center;padding:6px 0;border-bottom:1px solid var(--fm-br,#1E1E1E);font-size:12px;">
                  <a href="javascript:void(0)" @click="$router.push('/orders/'+s.id)" style="color:#BFA164;text-decoration:none;font-family:monospace;">{{ s.order_number }}</a>
                  <span style="color:var(--fm-m,#A09888);">{{ (s.currency || 'KES') }} {{ formatPrice(s.total_amount) }}</span>
                  <span class="od-status" :class="'st-'+s.status" style="font-size:10px;">{{ (s.status||'').replace(/_/g,' ') }}</span>
                </div>
              </div>

              <div class="d-flex gap-2 flex-wrap">
                <button class="btn btn-sm btn-outline-warning" @click="load" :disabled="loading"><i class="mdi mdi-refresh me-1"></i>Refresh status</button>
                <button v-if="!groupPaymentCompleted" class="btn btn-sm btn-warning" @click="resendCartStk" :disabled="resendingCart"><span v-if="resendingCart" class="spinner-border spinner-border-sm me-1"></span><i v-else class="mdi mdi-cellphone-wireless me-1"></i>Resend M-Pesa Prompt</button>
                <button v-if="!groupPaymentCompleted" class="btn btn-sm btn-outline-warning" @click="copyPayLink"><i class="mdi" :class="payLinkCopied ? 'mdi-check' : 'mdi-link-variant'"></i> {{ payLinkCopied ? 'Link copied!' : 'Copy payment link' }}</button>
              </div>

              <div v-if="resendCartError" class="mt-2" style="color:#FF6B6B;font-size:12px;">{{ resendCartError }}</div>
              <div v-if="resendCartSent" class="mt-2" style="color:#50C878;font-size:12px;"><i class="mdi mdi-check-circle me-1"></i>New M-Pesa prompt sent. Check your phone.</div>
            </div>

            <!-- PAYMENT NEEDED (single-order legacy flow — hidden when this order is part of a cart group) -->
            <div v-if="!order.order_group_id && (order.status==='pending_payment'||order.status==='payment_initiated'||order.status==='stk_sent'||order.status==='partially_paid')&&isBuyer">
              <div class="od-pay-box mb-3">
                <div class="od-pay-header"><i class="mdi mdi-cellphone me-1" style="color:#50C878;"></i> Pay via M-Pesa</div>
                <!-- Dev-Requests #7 — outstanding balance summary when partial payments are in play. -->
                <div v-if="Number(order.paid_amount||0) > 0" class="od-installment-summary mb-2">
                  <div class="d-flex justify-content-between" style="font-size:12px;">
                    <span>Total</span>
                    <span>{{ (order.currency || 'KES') }} {{ formatPrice(order.total_amount) }}</span>
                  </div>
                  <div class="d-flex justify-content-between" style="font-size:12px;color:#50C878;">
                    <span>Paid so far</span>
                    <span>{{ (order.currency || 'KES') }} {{ formatPrice(order.paid_amount) }}</span>
                  </div>
                  <div class="d-flex justify-content-between" style="font-size:13px;font-weight:700;color:#FF9800;margin-top:4px;padding-top:4px;border-top:1px dashed rgba(255,152,0,0.35);">
                    <span>Outstanding</span>
                    <span>{{ (order.currency || 'KES') }} {{ formatPrice(outstandingAmount) }}</span>
                  </div>
                </div>
                <div class="od-pay-amount">{{ (order.currency || 'KES') }} {{ formatPrice(outstandingAmount) }}</div>
                <!-- Dev-Requests #7 — installment toggle. When ON, buyer edits `payAmount`
                     to any positive value ≤ outstanding, so they can pay in chunks. -->
                <div class="mt-2 d-flex align-items-center" style="gap:8px;">
                  <label style="font-size:12px;color:var(--fm-m,#A09888);"><input type="checkbox" v-model="payAsInstallment" @change="onToggleInstallment"/> Pay in installments</label>
                </div>
                <div v-if="payAsInstallment" class="mt-2">
                  <label class="p2s" style="margin-bottom:4px;display:block;">Installment Amount ({{ order.currency || 'KES' }})</label>
                  <input v-model.number="payAmount" type="number" min="1" :max="outstandingAmount" step="1" style="width:100%;background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#2A2520);border-radius:8px;padding:10px 12px;color:#1A1A1A;font-size:14px;" />
                  <div class="p2s mt-1" v-if="payAmount > outstandingAmount" style="color:#FF6B6B;">
                    Amount exceeds outstanding balance. Max: {{ formatPrice(outstandingAmount) }}
                  </div>
                </div>
                <div style="margin-top:12px;">
                  <label class="p2s" style="margin-bottom:4px;display:block;">M-Pesa Phone Number</label>
                  <div style="display:flex;gap:8px;">
                    <div style="flex:1;position:relative;">
                      <span style="position:absolute;left:12px;top:50%;transform:translateY(-50%);color:var(--fm-s,#777);font-size:13px;">+254</span>
                      <input v-model="payPhone" type="tel" maxlength="10" placeholder="7XX XXX XXX" style="width:100%;background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#2A2520);border-radius:8px;padding:10px 12px 10px 52px;color:#1A1A1A;font-size:14px;" />
                    </div>
                    <button class="btn btn-warning fw-bold" @click="sendStkFromOrder" :disabled="stkSending || (payAsInstallment && (!payAmount || payAmount > outstandingAmount))" style="white-space:nowrap;">
                      <span v-if="stkSending"><span class="spinner-border spinner-border-sm me-1"></span></span>
                      <span v-else><i class="mdi mdi-send me-1"></i>Send Prompt</span>
                    </button>
                  </div>
                </div>

                <!-- STK STATUS -->
                <div v-if="stkStatus==='sent'" class="od-stk-status mt-3" style="background:rgba(80,200,120,0.06);border-color:rgba(80,200,120,0.2);">
                  <div class="d-flex align-items-center gap-2"><div class="ck-pulse"></div><span style="color:#50C878;font-weight:600;font-size:13px;">Waiting for M-Pesa PIN</span></div>
                  <div class="p2s mt-1">Check <strong style="color:var(--fm-h,#F5F0E8);">+254{{ payPhone }}</strong> and enter PIN</div>
                  <div class="p2s mt-1"><i class="mdi mdi-refresh mdi-spin me-1"></i>Checking... ({{ stkCountdown }}s)</div>
                </div>
                <div v-if="stkStatus==='completed'" class="od-stk-status mt-3" style="background:rgba(80,200,120,0.08);border-color:rgba(80,200,120,0.2);">
                  <i class="mdi mdi-check-circle" style="color:#50C878;font-size:24px;"></i>
                  <span style="color:#50C878;font-weight:600;">Payment Confirmed!</span>
                  <div v-if="stkReceipt" class="p2s mt-1">Receipt: <strong style="color:var(--fm-h,#F5F0E8);">{{ stkReceipt }}</strong></div>
                </div>
                <div v-if="stkStatus==='failed'||stkStatus==='cancelled'" class="od-stk-status mt-3" style="background:rgba(255,107,107,0.06);border-color:rgba(255,107,107,0.15);">
                  <i class="mdi mdi-close-circle" style="color:#FF6B6B;font-size:20px;"></i>
                  <span style="color:#FF6B6B;font-weight:600;">{{ stkStatus==='cancelled'?'Cancelled':'Failed' }}</span>
                  <div class="p2s mt-1">{{ stkError || 'Try again with a different number.' }}</div>
                </div>
              </div>

              <!-- MANUAL PAYBILL OPTION -->
              <div class="od-pay-box">
                <div style="font-size:12px;color:var(--fm-s,#777);margin-bottom:8px;"><i class="mdi mdi-bank me-1"></i>Or pay manually via M-Pesa Paybill</div>
                <div class="od-paybill-rows">
                  <div><span>Paybill</span><strong style="color:#BFA164;">4160303</strong></div>
                  <div><span>Account</span><strong>{{ order.order_number }}</strong></div>
                  <div><span>Amount</span><strong style="color:#50C878;">{{ (order.currency || 'KES') }} {{ formatPrice(order.total_amount) }}</strong></div>
                </div>
                <button class="btn btn-sm btn-outline-warning mt-2 w-100" @click="copyPayLink"><i class="mdi" :class="payLinkCopied ? 'mdi-check' : 'mdi-link-variant'"></i> {{ payLinkCopied ? 'Link copied!' : 'Copy payment link' }}</button>
                <div style="font-size:10px;color:var(--fm-s,#777);margin-top:4px;">Share this link with anyone paying on your behalf — no login needed.</div>
              </div>

              <button class="btn btn-outline-danger btn-sm w-100 mt-3" @click="confirmAction('cancelled','Cancel Order','Are you sure you want to cancel this order?','danger')"><i class="mdi mdi-close me-1"></i>Cancel Order</button>
            </div>

            <!-- SELLER ACTIONS -->
            <div v-else-if="order.status==='paid'&&isSeller" class="d-grid gap-2"><button class="btn btn-warning fw-bold" @click="confirmAction('confirmed','Confirm Order','You are confirming this order and will begin preparing it for the buyer. Proceed?','success')"><i class="mdi mdi-check me-1"></i>Confirm & Prepare</button><button class="btn btn-outline-danger btn-sm" @click="confirmAction('cancelled','Decline Order','Are you sure you want to decline this order? The buyer will be refunded.','danger')">Decline Order</button></div>
            <div v-else-if="order.status==='confirmed'&&isSeller" class="d-grid gap-2">
              <button class="btn btn-warning fw-bold" :disabled="!complianceReady" @click="confirmAction('in_transit','Mark In Transit','Confirm that this order has been shipped or is on the way to the buyer.','success')">
                <i class="mdi mdi-truck-fast me-1"></i>Mark Shipped / In Transit
              </button>
              <div v-if="!complianceReady" style="font-size:11px;color:#FF9800;text-align:center;">
                <i class="mdi mdi-alert-circle-outline me-1"></i>Upload Movement Permit + No Objection Certificate first.
              </div>
            </div>
            <!-- Issue 22: transporter invoked In Transit — seller approves the handover -->
            <div v-else-if="order.status==='pending_seller_confirmation'&&isSeller" class="d-grid gap-2">
              <div style="background:rgba(191,161,100,.08);border:1px solid rgba(191,161,100,.25);border-radius:10px;padding:10px 12px;font-size:12px;color:var(--fm-m,#A09888);">
                <i class="mdi mdi-truck-check me-1" style="color:#BFA164;"></i>The transporter has picked up the animals. Review the pickup record below and confirm the handover to move this order to In Transit.
              </div>
              <button class="btn btn-warning fw-bold" :disabled="!complianceReady" @click="confirmAction('in_transit','Confirm Handover','You are confirming that the transporter picked up the animals as recorded. The order will move to In Transit.','success')">
                <i class="mdi mdi-check-decagram me-1"></i>Confirm Handover — In Transit
              </button>
              <div v-if="!complianceReady" style="font-size:11px;color:#FF9800;text-align:center;">
                <i class="mdi mdi-alert-circle-outline me-1"></i>Upload Movement Permit + No Objection Certificate first.
              </div>
            </div>
            <div v-else-if="order.status==='pending_seller_confirmation'&&isBuyer" class="text-center py-2">
              <i class="mdi mdi-truck-fast" style="font-size:28px;color:#BFA164;"></i>
              <div class="p2s mt-2">The transporter has picked up your animals. Waiting for the seller to confirm the handover.</div>
            </div>
            <div v-else-if="order.status==='in_transit'&&isSeller" class="d-grid"><button class="btn btn-warning fw-bold" @click="confirmAction('delivered','Mark Delivered','Confirm that the animal has been delivered to the buyer. The buyer will have 48 hours to inspect.','success')"><i class="mdi mdi-truck-check me-1"></i>Mark Delivered</button></div>
            <!-- AUTO-RELEASE COUNTDOWN (shown to both buyer & seller) -->
            <div v-if="(order.status==='delivered'||order.status==='inspection')&&releaseSeconds>0" class="od-countdown mb-3">
              <div class="od-countdown-icon"><i class="mdi mdi-timer-sand"></i></div>
              <div class="od-countdown-label">{{ isBuyer ? 'Inspection window closes in' : 'Auto-release in' }}</div>
              <div class="od-countdown-time">{{ releaseDisplay }}</div>
              <div class="od-countdown-bar"><div class="od-countdown-fill" :style="{width: releasePct+'%'}"></div></div>
              <div class="od-countdown-hint" v-if="isBuyer">Confirm receipt or raise a dispute before the timer runs out. Payment will auto-release to the seller when this expires.</div>
              <div class="od-countdown-hint" v-else>Payment will be released to your wallet once the inspection window expires, unless the buyer raises a dispute.</div>
            </div>
            <div v-else-if="(order.status==='delivered'||order.status==='inspection')&&releaseExpired" class="od-countdown od-countdown-expired mb-3">
              <div class="od-countdown-icon"><i class="mdi mdi-check-circle"></i></div>
              <div class="od-countdown-label">Inspection window expired</div>
              <div class="od-countdown-hint">Payment is being auto-released to the seller.</div>
            </div>

            <div v-if="(order.status==='delivered'||order.status==='inspection')&&isBuyer" class="d-grid gap-2"><button class="btn btn-success fw-bold" @click="confirmAction('completed','Release Payment','You are confirming that you received the animal and are satisfied. Payment of KES '+formatPrice(sellerPayout)+' will be released to the seller. This cannot be undone.','warning')"><i class="mdi mdi-check-all me-1"></i>Confirm Receipt & Release Payment</button><button class="btn btn-outline-danger btn-sm" @click="raiseDispute"><i class="mdi mdi-alert me-1"></i>Raise Dispute</button></div>
            <div v-else-if="order.status==='completed'" class="text-center py-2"><i class="mdi mdi-check-circle" style="font-size:28px;color:#50C878;"></i><div class="p2s mt-2">Order completed</div><button v-if="isBuyer" class="btn btn-warning btn-sm mt-2" @click="leaveReview"><i class="mdi mdi-star me-1"></i>Leave Review</button></div>
            <div v-else class="p2s text-center py-2">No actions available</div>
            <div v-if="actMsg" class="mt-2 text-center" :style="{color:actOk?'#50C878':'#FF6B6B',fontSize:'12px'}">{{ actMsg }}</div>
          </div>

          <!-- Issue 22: pickup record — what the transporter loaded at the farm -->
          <div v-if="order.pickup_record || orderPickupPhotos.length" class="p2c mb-3">
            <h6 class="p2h"><i class="mdi mdi-clipboard-check-outline me-2"></i>Pickup Record</h6>
            <div v-if="order.pickup_record" style="font-size:13px;color:var(--fm-h,#F5F0E8);white-space:pre-wrap;">{{ order.pickup_record }}</div>
            <div v-if="orderPickupPhotos.length" class="d-flex gap-2 mt-2" style="flex-wrap:wrap;">
              <a v-for="(ph,i) in orderPickupPhotos" :key="i" :href="pu(ph)" target="_blank">
                <img :src="pu(ph)" style="width:76px;height:76px;object-fit:cover;border-radius:8px;border:1px solid var(--fm-br,#1E1E1E);" />
              </a>
            </div>
            <div v-if="order.actual_pickup_at" class="p2s mt-2" style="font-size:10px;">Picked up {{ fmtDate(order.actual_pickup_at) }}</div>
          </div>

          <!-- DISPUTE & SUPPORT — journey spec: dispute channels visible at every stage -->
          <div class="p2c mb-3">
            <h6 class="p2h"><i class="mdi mdi-lifebuoy me-2"></i>Problem with this order?</h6>
            <p class="p2s" style="margin-bottom:12px;">
              <span v-if="canDisputeNow">Your 48-hour inspection window is open. If something is wrong, raise a dispute now — payment stays frozen in escrow while we review.</span>
              <span v-else-if="order.status==='disputed'">This order is under dispute resolution. Track progress or add evidence from the disputes page.</span>
              <span v-else-if="order.status==='completed'||order.status==='cancelled'">This order is closed. If you still need help, our support team is available below.</span>
              <span v-else>Disputes can be raised in-app during the 48-hour inspection window after delivery. Need help before then? Call or SMS support any time.</span>
            </p>
            <div class="d-grid gap-2">
              <button v-if="canDisputeNow" class="btn btn-outline-danger btn-sm" @click="raiseDispute"><i class="mdi mdi-shield-alert me-1"></i>Raise a Dispute</button>
              <button v-if="order.status==='disputed'" class="btn btn-outline-danger btn-sm" @click="$router.push('/disputes')"><i class="mdi mdi-gavel me-1"></i>View Dispute</button>
              <a v-if="supportPhone" :href="'tel:'+supportPhone" class="btn btn-outline-secondary btn-sm"><i class="mdi mdi-phone me-1"></i>Call Support &middot; {{ supportPhone }}</a>
              <a v-if="supportPhone" :href="'sms:'+supportPhone" class="btn btn-outline-secondary btn-sm"><i class="mdi mdi-message-text me-1"></i>SMS Support &middot; {{ supportPhone }}</a>
            </div>
          </div>

          <!-- COMPLIANCE UPLOAD MODAL -->
          <div v-if="showComplianceUpload" class="od-modal-overlay" @click.self="!uploadingCompliance?showComplianceUpload=false:null" style="overflow-y:auto;align-items:flex-start;padding:40px 16px;">
            <div class="od-modal" style="max-width:520px;">
              <h6 style="color:var(--fm-h,#F5F0E8);font-weight:700;">
                <i class="mdi mdi-file-certificate me-1" style="color:#BFA164;"></i>Upload {{ complianceForm.docLabel }}
              </h6>
              <p style="color:var(--fm-m,#A09888);font-size:12px;margin-bottom:14px;">
                Take a clear photo of the original document. Fill in the reference number and dates so both you and the buyer have a searchable record.
              </p>
              <div class="mb-2">
                <label class="fm-label" style="font-size:11px;color:var(--fm-s,#6B6356);text-transform:uppercase;">Photo / scan *</label>
                <input type="file" ref="complianceFile" accept="image/*" @change="onComplianceFileChange" class="form-control" />
                <div v-if="complianceForm.previewUrl" style="margin-top:8px;">
                  <img :src="complianceForm.previewUrl" style="max-height:180px;border-radius:6px;border:1px solid #2A2520;" />
                </div>
              </div>
              <div class="row g-2">
                <div class="col-6"><label class="fm-label" style="font-size:11px;color:var(--fm-s,#6B6356);text-transform:uppercase;">Ref no.</label><input v-model="complianceForm.permitRefNo" class="form-control" placeholder="e.g. 1625749" /></div>
                <div class="col-6"><label class="fm-label" style="font-size:11px;color:var(--fm-s,#6B6356);text-transform:uppercase;">Head count</label><input v-model.number="complianceForm.headCount" type="number" min="1" class="form-control" /></div>
                <div :class="complianceForm.docType==='veterinary_health_cert' ? 'col-7' : 'col-12'"><label class="fm-label" style="font-size:11px;color:var(--fm-s,#6B6356);text-transform:uppercase;">Issued by (officer)</label><input v-model="complianceForm.issuedBy" class="form-control" placeholder="e.g. James Mutemi, Kasarani SCVO" /></div>
                <div v-if="complianceForm.docType==='veterinary_health_cert'" class="col-5"><label class="fm-label" style="font-size:11px;color:var(--fm-s,#6B6356);text-transform:uppercase;">KVB number</label><input v-model="complianceForm.kvbNumber" class="form-control" placeholder="e.g. KVB/1234" /></div>
                <div class="col-6"><label class="fm-label" style="font-size:11px;color:var(--fm-s,#6B6356);text-transform:uppercase;">Issued on</label><input v-model="complianceForm.issuedAt" type="date" class="form-control" /></div>
                <div class="col-6"><label class="fm-label" style="font-size:11px;color:var(--fm-s,#6B6356);text-transform:uppercase;">Valid until</label><input v-model="complianceForm.validUntil" type="date" class="form-control" /></div>
                <div class="col-6"><label class="fm-label" style="font-size:11px;color:var(--fm-s,#6B6356);text-transform:uppercase;">From (sub-county)</label><input v-model="complianceForm.fromLocation" class="form-control" placeholder="e.g. Rumuruti, Laikipia" /></div>
                <div class="col-6"><label class="fm-label" style="font-size:11px;color:var(--fm-s,#6B6356);text-transform:uppercase;">To (sub-county)</label><input v-model="complianceForm.toLocation" class="form-control" placeholder="e.g. Kahawa West, Nairobi" /></div>
                <div class="col-12"><label class="fm-label" style="font-size:11px;color:var(--fm-s,#6B6356);text-transform:uppercase;">Route (optional)</label><input v-model="complianceForm.route" class="form-control" placeholder="e.g. Vehicle KCC 1284D" /></div>
                <div class="col-12"><label class="fm-label" style="font-size:11px;color:var(--fm-s,#6B6356);text-transform:uppercase;">Special conditions</label><input v-model="complianceForm.specialConditions" class="form-control" placeholder="e.g. Slaughter · Breeding" /></div>
              </div>
              <div v-if="complianceForm.error" style="color:#FF6B6B;font-size:12px;margin-top:10px;"><i class="mdi mdi-alert-circle-outline me-1"></i>{{ complianceForm.error }}</div>
              <div class="d-flex gap-2 mt-3">
                <button class="btn btn-outline-secondary flex-fill" @click="showComplianceUpload=false" :disabled="uploadingCompliance">Cancel</button>
                <button class="btn btn-warning fw-bold flex-fill" @click="submitCompliance" :disabled="uploadingCompliance || !complianceForm.file">
                  <span v-if="uploadingCompliance"><span class="spinner-border spinner-border-sm me-1"></span>Uploading...</span>
                  <span v-else><i class="mdi mdi-upload me-1"></i>Upload</span>
                </button>
              </div>
            </div>
          </div>

          <!-- CONFIRMATION MODAL -->
          <div v-if="showConfirm" class="od-modal-overlay" @click.self="!actLoading?showConfirm=false:null">
            <div class="od-modal">
              <div class="od-modal-icon" :class="'od-modal-'+confirmType">
                <i :class="confirmType==='danger'?'mdi mdi-alert-circle':confirmType==='warning'?'mdi mdi-alert':'mdi mdi-check-circle'"></i>
              </div>
              <h6 class="od-modal-title">{{ confirmTitle }}</h6>
              <p class="od-modal-msg">{{ confirmMsg }}</p>
              <!-- Client issue 33: seller inputs estimated arrival when marking in transit -->
              <div v-if="confirmStatus==='in_transit'" class="mt-3 text-start">
                <label class="form-label" style="font-size:.85rem;font-weight:600;">Estimated arrival (date &amp; time) *</label>
                <input type="datetime-local" class="form-control" v-model="etaInput">
                <div v-if="etaError" class="text-danger mt-1" style="font-size:.8rem;">{{ etaError }}</div>
              </div>
              <!-- Extra warning for payment release -->
              <div v-if="confirmStatus==='completed'" class="od-release-box mt-3">
                <div class="od-release-row"><span>Amount to seller</span><strong style="color:#50C878;">KES {{ formatPrice(sellerPayout) }}</strong></div>
                <div class="od-release-row"><span>Seller</span><strong style="color:var(--fm-h,#F5F0E8);">{{ order.seller_name }}</strong></div>
                <div class="od-release-row"><span>Order</span><strong style="color:#BFA164;">{{ order.order_number }}</strong></div>
              </div>
              <div class="d-flex gap-2 mt-3">
                <button class="btn btn-outline-secondary flex-fill" @click="showConfirm=false" :disabled="actLoading">Cancel</button>
                <button class="btn flex-fill fw-bold" :class="confirmType==='danger'?'btn-danger':confirmType==='warning'?'btn-warning text-dark':'btn-success'" @click="executeConfirmed" :disabled="actLoading">
                  <span v-if="actLoading"><span class="spinner-border spinner-border-sm me-1"></span>Processing...</span>
                  <span v-else>{{ confirmTitle }}</span>
                </button>
              </div>
            </div>
          </div>

          <!-- PAYMENT RELEASED SUCCESS MODAL -->
          <div v-if="showReleaseSuccess" class="od-modal-overlay" @click.self="showReleaseSuccess=false">
            <div class="od-modal">
              <div class="od-release-success-icon"><i class="mdi mdi-check-bold"></i></div>
              <h5 style="color:#50C878;font-weight:700;margin-bottom:4px;">Payment Released!</h5>
              <p class="od-modal-msg">You have confirmed receipt of the animal.</p>
              <div class="od-release-box mt-3">
                <div class="od-release-row"><span>Released to seller</span><strong style="color:#50C878;">KES {{ formatPrice(releasedAmount) }}</strong></div>
                <div class="od-release-row"><span>Seller</span><strong style="color:var(--fm-h,#F5F0E8);">{{ releasedSeller }}</strong></div>
                <div class="od-release-row"><span>Order</span><strong style="color:#BFA164;">{{ releasedOrder }}</strong></div>
              </div>
              <div style="background:rgba(80,200,120,0.06);border:1px solid rgba(80,200,120,0.15);border-radius:8px;padding:10px 12px;margin-top:14px;">
                <div style="font-size:11px;color:#666666;"><i class="mdi mdi-shield-check me-1" style="color:#50C878;"></i>Transaction complete. Funds have been credited to the seller's wallet.</div>
              </div>
              <button class="btn btn-success fw-bold w-100 mt-3" @click="showReleaseSuccess=false"><i class="mdi mdi-check me-1"></i>Done</button>
            </div>
          </div>
          <button v-if="otherPartyId" class="btn btn-outline-warning w-100" @click="messageOther"><i class="mdi mdi-message-text me-1"></i>Message {{ isBuyer?'Seller':'Buyer' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>
<style>

/* === GLOBAL THEME === */
html[data-theme="light"] body { background:#FFFFFF!important; color:#1A1A1A!important; }
html[data-theme="light"] .fm-card,html[data-theme="light"] .p2c,html[data-theme="light"] .ld-section,html[data-theme="light"] .ld-action-card,html[data-theme="light"] .ld-seller-card,html[data-theme="light"] .ld-info-card,html[data-theme="light"] .ld-related-card { background:#FFFFFF!important; border-color:#E8E2DA!important; }
html[data-theme="light"] .fm-overlay .fm-modal,html[data-theme="light"] .ad-overlay .ad-modal { background:#FFFFFF!important; border-color:#E8E2DA!important; }
html[data-theme="light"] .fm-input,html[data-theme="light"] .fm-select { background:#F7F5F2!important; border-color:#E0D8D0!important; color:#1A1A1A!important; }
html[data-theme="light"] .fm-label { color:#555!important; }
html[data-theme="dark"] body { background:#0A0A0A!important; color:#F5F0E8!important; }
</style>
<style>
:root{--fm-h:#F5F0E8;--fm-s:#6B6356;--fm-m:#A09888;--fm-bg1:#141414;--fm-bg2:#0F0F0F;--fm-bg3:#1E1E1E;--fm-br:#1E1E1E}
html[data-theme="light"]{--fm-h:#1A1A1A;--fm-s:#777;--fm-m:#555;--fm-bg1:#FFFFFF;--fm-bg2:#F7F5F2;--fm-bg3:#F0EBE4;--fm-br:#E8E2DA}
html[data-theme="light"] body{background:#F7F5F2!important;color:#1A1A1A!important}
html[data-theme="light"] .card{background:#FFFFFF!important;border-color:#E8E2DA!important}
html[data-theme="light"] .card-body{color:#1A1A1A!important}
html[data-theme="light"] .modal-content{background:#FFFFFF!important;border-color:#E8E2DA!important;color:#1A1A1A!important}
html[data-theme="light"] .form-control,html[data-theme="light"] .form-select{background:#FFFFFF!important;border-color:#E0D8D0!important;color:#1A1A1A!important}
html[data-theme="light"] .table{color:#1A1A1A!important}
html[data-theme="light"] .table th{color:#555!important;border-color:#E8E2DA!important}
html[data-theme="light"] .table td{border-color:#F0EBE4!important}
html[data-theme="light"] [style*="color:#F5F0E8"]{color:#1A1A1A!important}
html[data-theme="light"] [style*="color:#6B6356"]{color:#777!important}
html[data-theme="light"] [style*="color:#A09888"]{color:#555!important}
html[data-theme="light"] [style*="background:#0F0F0F"]{background:#F7F5F2!important}
html[data-theme="light"] [style*="background:#141414"]{background:#FFFFFF!important}
html[data-theme="light"] [style*="background:#1E1E1E"]{background:#F0EBE4!important}
html[data-theme="light"] [style*="background:#0A0A0A"]{background:#F7F5F2!important}
html[data-theme="light"] [style*="background:#1a1510"]{background:#FFF8F0!important}
html[data-theme="light"] [style*="background:#1A1A1A"]{background:#F0EBE4!important}
html[data-theme="light"] [style*="border:1px solid #1E1E1E"]{border-color:#E8E2DA!important}
html[data-theme="light"] [style*="border:1px solid #2A2520"]{border-color:#E0D8D0!important}
html[data-theme="light"] [style*="border-bottom:1px solid #1A1A1A"]{border-bottom-color:#F0EBE4!important}
html[data-theme="light"] [style*="border-bottom:1px solid #1E1E1E"]{border-bottom-color:#F0EBE4!important}
html[data-theme="light"] [style*="border-bottom:1px solid #2A2520"]{border-bottom-color:#E0D8D0!important}
html[data-theme="light"] [style*="border-top:1px solid #2A2520"]{border-top-color:#E0D8D0!important}
html[data-theme="light"] [style*="color:#BFA164"]{color:#BFA164!important}
html[data-theme="light"] [style*="color:#50C878"]{color:#50C878!important}
html[data-theme="light"] [style*="color:#4FC3F7"]{color:#4FC3F7!important}
html[data-theme="light"] [style*="color:#FF6B6B"]{color:#FF6B6B!important}
html[data-theme="light"] [style*="color:#FF9800"]{color:#FF9800!important}
html[data-theme="light"] [style*="color:#CE93D8"]{color:#CE93D8!important}
</style>

<style>
:root{
  --fm-h:#F5F0E8;
  --fm-s:#6B6356;
  --fm-m:#A09888;
  --fm-bg1:#141414;
  --fm-bg2:#0F0F0F;
  --fm-bg3:#1E1E1E;
  --fm-br:#1E1E1E;
  --fm-page:#0A0A0A;
}
html[data-theme="light"]{
  --fm-h:#1A1A1A;
  --fm-s:#777;
  --fm-m:#555;
  --fm-bg1:#FFFFFF;
  --fm-bg2:#F7F5F2;
  --fm-bg3:#F0EBE4;
  --fm-br:#E8E2DA;
  --fm-page:#F7F5F2;
}
html[data-theme="light"] body,
html[data-theme="light"] #layout-wrapper,
html[data-theme="light"] .ord-root,
html[data-theme="light"] .ord-container{
  background:var(--fm-page)!important;
  color:var(--fm-h)!important;
}
html[data-theme="dark"] body,
html[data-theme="dark"] #layout-wrapper,
html[data-theme="dark"] .ord-root,
html[data-theme="dark"] .ord-container{
  background:var(--fm-page)!important;
  color:var(--fm-h)!important;
}
html[data-theme="light"] .ord-topbar{
  background:#FFFFFF!important;
  border-bottom-color:#E8E2DA!important;
}
html[data-theme="dark"] .ord-topbar{
  background:var(--fm-bg1,#141414)!important;
}
html[data-theme="light"] .p2c,
html[data-theme="light"] .od-modal,
html[data-theme="light"] .od-pay-box,
html[data-theme="light"] .od-countdown,
html[data-theme="light"] .od-release-box{
  background:#FFFFFF!important;
  border-color:#E8E2DA!important;
  color:#1A1A1A!important;
}
html[data-theme="light"] .p2h,
html[data-theme="light"] .od-modal-title,
html[data-theme="light"] .od-pay-header,
html[data-theme="light"] .od-paybill-rows strong,
html[data-theme="light"] .od-release-row strong{
  color:#1A1A1A!important;
}
html[data-theme="light"] .p2s,
html[data-theme="light"] .p2l,
html[data-theme="light"] .od-modal-msg,
html[data-theme="light"] .od-countdown-label,
html[data-theme="light"] .od-countdown-hint,
html[data-theme="light"] .od-release-row{
  color:#555!important;
}
html[data-theme="light"] .p2t{
  background:#F0EBE4!important;
}
html[data-theme="light"] .od-paybill-rows>div{
  background:#F7F5F2!important;
  color:#555!important;
}
html[data-theme="light"] input,
html[data-theme="light"] textarea,
html[data-theme="light"] select{
  background:#FFFFFF!important;
  color:#1A1A1A!important;
  border-color:#E0D8D0!important;
}
html[data-theme="light"] .od-countdown-time{
  color:#1A1A1A!important;
}
html[data-theme="light"] .tli{border-left-color:#E0D8D0!important;}
html[data-theme="light"] .tld{background:#E0D8D0!important;border-color:#E0D8D0!important;}
html[data-theme="light"] .tli.done .tld{background:#50C878!important;border-color:#50C878!important;}
</style>

<style scoped>
.ord-root{background:var(--fm-page,#0A0A0A)!important;min-height:100vh;color:var(--fm-h,#F5F0E8)}
.ord-topbar{position:sticky;top:0;z-index:100;padding:12px 0;background:var(--fm-bg1,#141414)!important;backdrop-filter:blur(10px);border-bottom:1px solid var(--fm-br,#1E1E1E)}
.ord-container{padding-top:80px;padding-bottom:60px;max-width:1000px;}
.p2c{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:12px;padding:20px}
.p2h{font-size:14px;font-weight:700;color:var(--fm-h,#F5F0E8);margin-bottom:14px}
.cmp-slot{background:rgba(191,161,100,0.05);border:1px solid rgba(191,161,100,0.15);border-radius:8px;padding:10px 12px;margin-bottom:8px}
.p2s{font-size:12px;color:var(--fm-m,#A09888)}
.p2t{width:60px;height:60px;border-radius:8px;overflow:hidden;background:var(--fm-bg2,#0F0F0F);flex-shrink:0;display:flex;align-items:center;justify-content:center}
.p2t img{width:100%;height:100%;object-fit:cover}
.p2l{display:flex;justify-content:space-between;padding:7px 0;font-size:13px;color:var(--fm-m,#A09888);border-bottom:1px solid var(--fm-br,#1E1E1E)}
.p2b{font-size:10px;font-weight:600;padding:2px 8px;border-radius:10px;text-transform:capitalize}
.st-pending_payment{background:rgba(255,171,0,.15);color:#FFAB00}
.st-paid{background:rgba(80,200,120,.15);color:#50C878}
.st-confirmed{background:rgba(80,165,241,.15);color:#50a5f1}
.st-delivered{background:rgba(155,89,182,.15);color:#9b59b6}
.st-completed{background:rgba(80,200,120,.2);color:#50C878}
.st-cancelled{background:rgba(255,107,107,.15);color:#FF6B6B}
.st-payment_initiated{background:rgba(255,171,0,.15);color:#FFAB00}
.st-stk_sent{background:rgba(255,171,0,.15);color:#FFAB00}
.st-in_transit{background:rgba(155,89,182,.15);color:#9b59b6}
.st-pending_seller_confirmation{background:rgba(191,161,100,.15);color:#BFA164}
.st-inspection{background:rgba(80,165,241,.15);color:#50a5f1}
.st-disputed{background:rgba(255,107,107,.15);color:#FF6B6B}
.tli{position:relative;padding-bottom:20px;padding-left:16px;border-left:2px solid #2A2520}
.tli.done{border-left-color:#50C878}
.tli:last-child{border-left:none;padding-bottom:0}
.tld{position:absolute;left:-7px;top:2px;width:12px;height:12px;border-radius:50%;background:#2A2520;border:2px solid #2A2520}
.tli.done .tld{background:#50C878;border-color:#50C878}
.od-modal-overlay{position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.7);z-index:2000;display:flex;align-items:center;justify-content:center;padding:20px;backdrop-filter:blur(4px)}
.od-modal{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#2A2520);border-radius:14px;padding:28px;max-width:400px;width:100%;text-align:center;animation:modalIn .2s ease-out}
@keyframes modalIn{from{opacity:0;transform:scale(0.9)}to{opacity:1;transform:scale(1)}}
.od-modal-icon{width:56px;height:56px;border-radius:50%;display:flex;align-items:center;justify-content:center;margin:0 auto 14px;font-size:28px}
.od-modal-success{background:rgba(80,200,120,0.12);color:#50C878}
.od-modal-warning{background:rgba(255,171,0,0.12);color:#FFAB00}
.od-modal-danger{background:rgba(255,107,107,0.12);color:#FF6B6B}
.od-modal-title{color:var(--fm-h,#F5F0E8);font-size:16px;font-weight:700;margin-bottom:8px}
.od-modal-msg{color:var(--fm-m,#A09888);font-size:13px;line-height:1.5;margin:0}
.od-release-box{background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#1E1E1E);border-radius:10px;padding:12px;display:flex;flex-direction:column;gap:8px}
.od-release-row{display:flex;justify-content:space-between;align-items:center;font-size:13px;color:var(--fm-m,#A09888)}
.od-release-row strong{font-size:14px}
.od-release-success-icon{width:64px;height:64px;border-radius:50%;background:rgba(80,200,120,0.15);display:flex;align-items:center;justify-content:center;margin:0 auto 14px}
.od-release-success-icon i{font-size:32px;color:#50C878}
.od-pay-box{background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#1E1E1E);border-radius:10px;padding:16px}
.od-pay-header{font-size:14px;font-weight:600;color:var(--fm-h,#F5F0E8);margin-bottom:4px}
.od-pay-amount{font-size:24px;font-weight:700;color:#BFA164}
.od-stk-status{display:flex;flex-direction:column;align-items:center;gap:4px;padding:14px;border:1px solid;border-radius:8px;text-align:center}
.od-paybill-rows{display:flex;flex-direction:column;gap:6px}
.od-paybill-rows>div{display:flex;justify-content:space-between;padding:6px 10px;background:var(--fm-bg1,#141414);border-radius:6px;font-size:12px;color:#777777}
.od-paybill-rows strong{color:var(--fm-h,#F5F0E8);font-size:13px}
.ck-pulse{width:10px;height:10px;border-radius:50%;background:#50C878;animation:odpulse 1.5s infinite}
@keyframes odpulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:0.5;transform:scale(1.3)}}
.od-countdown{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#2A2520);border-radius:12px;padding:16px;text-align:center}
.od-countdown-icon{font-size:28px;color:#FFAB00;margin-bottom:4px}
.od-countdown-icon .mdi-check-circle{color:#50C878}
.od-countdown-label{font-size:12px;color:#666666;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:8px}
.od-countdown-time{font-size:32px;font-weight:700;color:#1A1A1A;font-family:monospace;letter-spacing:2px;margin-bottom:10px}
.od-countdown-bar{height:4px;background:#2A2520;border-radius:2px;overflow:hidden}
.od-countdown-fill{height:100%;background:linear-gradient(90deg,#FF6B6B,#FFAB00,#50C878);border-radius:2px;transition:width 1s linear}
.od-countdown-hint{font-size:11px;color:#777777;margin-top:10px;line-height:1.4}
.od-countdown-expired{border-color:#50C878}
.od-countdown-expired .od-countdown-icon{color:#50C878}
.od-countdown-expired .od-countdown-label{color:#50C878}

/* ===== MOBILE — feels-like-an-app responsiveness ===== */
@media (max-width: 768px) {
  .od-modal { padding: 18px; border-radius: 12px; }
  .od-modal-icon { width: 48px; height: 48px; font-size: 22px; margin-bottom: 10px; }
  .od-modal-title { font-size: 15px; }
  .od-modal-msg { font-size: 12px; }
  .od-pay-box { padding: 12px; border-radius: 10px; }
  .od-pay-amount { font-size: 22px; }
  .od-pay-header { font-size: 13px; }
  .od-paybill-rows > div { font-size: 11px; padding: 5px 8px; }
  .od-countdown { padding: 12px; }
  .od-countdown-time { font-size: 26px; letter-spacing: 1px; }
  .od-countdown-icon { font-size: 22px; }
  .od-countdown-hint { font-size: 11px; line-height: 1.4; }
  .od-modal-overlay { padding: 12px; }
}
</style>
<script>
/* eslint-disable */
import { execute, executeJson, flocker, uploads } from "@/api";

function applyOrderTheme(vm) {
  const t = document.documentElement.getAttribute('data-theme') || localStorage.getItem('flockr_theme') || (vm && vm.darkMode ? 'dark' : 'light');
  const isDark = t !== 'light';
  if (vm) vm.darkMode = isDark;
  window.__flockrSetTheme(isDark ? 'dark' : 'light');

  if (document.body) {
    document.body.setAttribute('data-sidebar', isDark ? 'dark' : 'light');
    document.body.style.background = isDark ? '#0A0A0A' : '#F7F5F2';
    document.body.style.color = isDark ? '#F5F0E8' : '#1A1A1A';
  }

  const wrapper = document.getElementById('layout-wrapper');
  if (wrapper) {
    wrapper.style.background = isDark ? '#0A0A0A' : '#F7F5F2';
    wrapper.style.color = isDark ? '#F5F0E8' : '#1A1A1A';
  }
}

export default {
  name:"OrderDetail",
  data(){return{
    darkMode:(document.documentElement.getAttribute('data-theme')||localStorage.getItem('flockr_theme')||'dark')!=='light',
    themeObserver:null,
    loading:true,loadError:'',order:{},actMsg:'',actOk:false,actLoading:false,showConfirm:false,confirmTitle:'',confirmMsg:'',confirmType:'success',confirmStatus:'',showReleaseSuccess:false,releasedAmount:0,releasedSeller:'',releasedOrder:'',etaInput:'',etaError:'',payPhone:'',stkSending:false,stkStatus:'',stkError:'',stkReceipt:'',stkRef:'',stkCheckoutId:'',stkTimer:null,stkCountdown:5,releaseSeconds:0,releaseTimer:null,releaseExpired:false,releaseTotalSecs:172800,statusPollTimer:null,payLinkCopied:false,
    // Dev-Requests #7 — split / partial payments
    payAsInstallment:false, payAmount:0,
    // Compliance documents (Movement Permit + No Objection Certificate for livestock sales)
    complianceDocs:[], showComplianceUpload:false, uploadingCompliance:false,
    complianceForm:{docType:'',docLabel:'',file:null,previewUrl:'',permitRefNo:'',issuedBy:'',kvbNumber:'',issuedAt:'',validUntil:'',headCount:'',fromLocation:'',toLocation:'',route:'',specialConditions:'',error:''},
    complianceSlots:[
      {key:'movement_permit', label:'Veterinary Movement Permit', required:true},
      {key:'no_objection_certificate', label:'No Objection Certificate', required:true},
      {key:'veterinary_health_cert', label:'Veterinary Health Certificate', required:false}
    ],
    // Cart-group resend state
    resendingCart:false, resendCartError:'', resendCartSent:false,
    supportPhone:'',
    steps:[{k:'paid',l:'Order Created'},{k:'paid',l:'Payment Received'},{k:'in_transit',l:'In Transit'},{k:'delivered',l:'Delivered'},{k:'inspection',l:'Inspection'},{k:'completed',l:'Completed'}]
  }},
  computed:{
    themePageStyle(){return this.darkMode?'background:#0A0A0A;min-height:100vh;color:#F5F0E8;':'background:#F7F5F2;min-height:100vh;color:#1A1A1A;'},
    groupPaymentCompleted(){return this.order && this.order.groupPayment && this.order.groupPayment.status === 'completed';},
    themeCardStyle(){return this.darkMode?'background:#141414!important;border:1px solid #1E1E1E!important;':'background:#FFFFFF!important;border:1px solid #E8E2DA!important;'},
    themeInputStyle(){return this.darkMode?'background:#0F0F0F;border:1px solid #2A2520;color:#F5F0E8;':'background:#FFFFFF;border:1px solid #E0D8D0;color:#1A1A1A;'},

    isBuyer(){return String(this.order.buyer_id)===sessionStorage.getItem('userId')},
    isSeller(){return String(this.order.seller_id)===sessionStorage.getItem('userId')},
    orderPickupPhotos(){try{var a=JSON.parse(this.order.pickup_photos||'[]');return Array.isArray(a)?a:[]}catch(e){return[]}},
    otherPartyId(){var id=this.isBuyer?this.order.seller_id:this.order.buyer_id;return(id&&id!=='undefined'&&id!=='null')?id:null},
    sellerPayout(){var amt=Number(this.order.subtotal||0)-Number(this.order.platform_fee||0);if(this.order.transport_option==='seller_delivers')amt+=Number(this.order.transport_fee||0);return Math.max(0,amt)},
    // Which species need Kenya movement/NOC docs before crossing sub-counties
    needsCompliance(){var s=(this.order.species_name||'').toLowerCase();return ['cattle','sheep','goat','goats','pig','pigs'].indexOf(s)>=0;},
    hasMovementPermit(){return !!this.complianceDocs.find(function(d){return d.doc_type==='movement_permit'});},
    hasNOC(){return !!this.complianceDocs.find(function(d){return d.doc_type==='no_objection_certificate'});},
    complianceReady(){return !this.needsCompliance || (this.hasMovementPermit && this.hasNOC);},
    releaseDisplay(){var s=this.releaseSeconds;if(s<=0)return'00:00:00';var h=Math.floor(s/3600);var m=Math.floor((s%3600)/60);var sec=s%60;return(h<10?'0':'')+h+':'+(m<10?'0':'')+m+':'+(sec<10?'0':'')+sec},
    releasePct(){if(this.releaseTotalSecs<=0)return 0;return Math.max(0,Math.min(100,(this.releaseSeconds/this.releaseTotalSecs)*100))},
    // Dev-Requests #7 — remaining amount owed on the order after any partial payments.
    outstandingAmount(){var tot=Number(this.order.total_amount||0);var paid=Number(this.order.paid_amount||0);return Math.max(0, Math.round((tot - paid) * 100) / 100)},
    canDisputeNow(){return (this.order.status==='delivered'||this.order.status==='inspection')&&this.isBuyer&&!this.releaseExpired}
  },
  mounted(){
    try { applyOrderTheme(this); } catch(e) { /* tolerate first-paint theme glitches */ }
    // Wrap the observer callback — without this, an exception during a Vue unmount mid-navigation
    // surfaces as an "Uncaught" error in the console with no stack pointing back here.
    this.themeObserver = new MutationObserver(() => {
      try { applyOrderTheme(this); } catch(e) { /* component may be unmounting; ignore */ }
    });
    try {
      this.themeObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['data-theme']
      });
    } catch(e) { /* unlikely — keep going */ }
    document.title = 'FLOCKR | Order';
    this.load();
    this.loadSupportInfo();
  },
  beforeUnmount(){if(this.themeObserver)this.themeObserver.disconnect();this.stopStkPoll();this.stopReleaseTimer();this.stopStatusPoll();},
  methods:{
    copyPayLink(){
      var ref=this.order.order_group_id||this.order.id;
      var self=this;
      navigator.clipboard.writeText(window.location.origin+'/pay/'+ref).then(function(){ self.payLinkCopied=true; setTimeout(function(){ self.payLinkCopied=false; },2500); });
    },
    pu(p){if(!p)return'';if(p.startsWith&&p.startsWith('http'))return p;return uploads+String(p).replace('uploads/','')},
    fN(v){return v?Number(v).toLocaleString():'0'},
    formatPrice(v){return v?Number(v).toLocaleString():'0'},
    fmtD(d){return d?new Date(d).toLocaleDateString('en-KE',{day:'numeric',month:'short',year:'numeric'}):''},
    fmtDate(d){if(!d)return'';var dt=new Date(String(d).replace(' ','T'));return dt.toLocaleDateString('en-KE',{day:'numeric',month:'short',year:'numeric'})+', '+dt.toLocaleTimeString('en-KE',{hour:'numeric',minute:'2-digit'})},
    isAf(st){if(!this.order||!this.order.status)return false;var o=['pending_payment','payment_initiated','stk_sent','partially_paid','paid','confirmed','pending_seller_confirmation','in_transit','delivered','inspection','completed'];return o.indexOf(this.order.status)>=o.indexOf(st)},
    docFor(docType){return this.complianceDocs.find(function(d){return d.doc_type===docType})},
    async loadSupportInfo(){
      try{
        var f=new FormData();f.append('function','getSupportInfo');
        var r=await execute(f,flocker);
        if(r.data&&r.data.success&&r.data.data) this.supportPhone=r.data.data.phone||'';
      }catch(e){/* support card just hides the phone links */}
    },
    async loadComplianceDocs(){
      if(!this.order || !this.order.id) return;
      try{
        var f=new FormData();f.append('function','getOrderComplianceDocs');f.append('orderId',this.order.id);
        var r=await execute(f,flocker);
        if(r.data && r.data.success && r.data.data) this.complianceDocs = r.data.data.documents || [];
      }catch(e){console.warn('loadComplianceDocs',e);}
    },
    openComplianceUpload(slot){
      this.complianceForm={docType:slot.key,docLabel:slot.label,file:null,previewUrl:'',permitRefNo:'',issuedBy:'',kvbNumber:'',issuedAt:'',validUntil:'',headCount:'',fromLocation:'',toLocation:'',route:'',specialConditions:'',error:''};
      // If a doc exists, prefill so the seller can update in place
      var existing=this.docFor(slot.key);
      if(existing){
        this.complianceForm.permitRefNo=existing.permit_ref_no||'';
        this.complianceForm.issuedBy=existing.issued_by||'';
        this.complianceForm.kvbNumber=existing.kvb_number||'';
        this.complianceForm.issuedAt=(existing.issued_at||'').substring(0,10);
        this.complianceForm.validUntil=(existing.valid_until||'').substring(0,10);
        this.complianceForm.headCount=existing.head_count||'';
        this.complianceForm.fromLocation=existing.from_location||'';
        this.complianceForm.toLocation=existing.to_location||'';
        this.complianceForm.route=existing.route||'';
        this.complianceForm.specialConditions=existing.special_conditions||'';
      }
      this.showComplianceUpload=true;
    },
    onComplianceFileChange(ev){
      var f=ev.target.files && ev.target.files[0]; if(!f) return;
      this.complianceForm.file=f;
      this.complianceForm.previewUrl=URL.createObjectURL(f);
    },
    async submitCompliance(){
      var c=this.complianceForm;
      if(!c.file){c.error='Please attach a photo of the document';return;}
      this.uploadingCompliance=true; c.error='';
      try{
        var userId=sessionStorage.getItem('userId');
        var fd=new FormData();
        fd.append('function','uploadOrderComplianceDoc');
        fd.append('orderId',this.order.id);
        fd.append('userId',userId);
        fd.append('docType',c.docType);
        fd.append('photo',c.file);
        fd.append('permitRefNo',c.permitRefNo||'');
        fd.append('issuedBy',c.issuedBy||'');
        fd.append('kvbNumber',c.kvbNumber||'');
        fd.append('issuedAt',c.issuedAt||'');
        fd.append('validUntil',c.validUntil||'');
        fd.append('headCount',c.headCount||'');
        fd.append('fromLocation',c.fromLocation||'');
        fd.append('toLocation',c.toLocation||'');
        fd.append('route',c.route||'');
        fd.append('specialConditions',c.specialConditions||'');
        var r=await execute(fd,flocker);
        if(r.data && r.data.success){
          this.showComplianceUpload=false;
          await this.loadComplianceDocs();
        }else{
          c.error=(r.data && r.data.message) || 'Upload failed';
        }
      }catch(e){c.error='Network error — please try again';}
      this.uploadingCompliance=false;
    },
    async resendCartStk(){
      this.resendCartError=''; this.resendCartSent=false;
      var userId = sessionStorage.getItem('userId');
      if (!userId || !this.order.order_group_id) return;
      // Use the previously-paid phone if available, else the buyer's profile phone, else prompt
      var phone = (this.order.groupPayment && this.order.groupPayment.payer_phone) || sessionStorage.getItem('userPhone') || this.order.buyer_phone || '';
      var entered = window.prompt('Enter the M-Pesa phone number to receive the prompt (use 07XX or 2547XX):', phone || '');
      if (!entered) return;
      var phoneRaw = String(entered).replace(/\D/g, '');
      if (phoneRaw.length < 9) { this.resendCartError = 'Invalid phone'; return; }
      var phoneNorm = phoneRaw.charAt(0) === '0' ? ('254' + phoneRaw.substring(1))
                    : (phoneRaw.length === 9 ? ('254' + phoneRaw) : phoneRaw);
      this.resendingCart = true;
      try {
        var f = new FormData();
        f.append('function','retryCartPayment');
        f.append('userId', userId);
        f.append('orderGroupId', this.order.order_group_id);
        f.append('phone', phoneNorm);
        var r = await execute(f, flocker);
        if (r.data && r.data.success) {
          this.resendCartSent = true;
          // Reload after a few seconds to pick up the new payment row
          var self = this; setTimeout(function(){ self.load(); }, 8000);
        } else {
          this.resendCartError = (r.data && r.data.message) || 'Could not resend the prompt';
        }
      } catch(e) {
        this.resendCartError = 'Network error — please try again';
      }
      this.resendingCart = false;
    },
    async load(){
      this.loading = true;
      this.loadError = '';
      try {
        var f = new FormData();
        f.append('function','getOrder');
        f.append('orderId', this.$route.params.id);
        var r = await execute(f, flocker);
        if (r.data && r.data.success) {
          this.order = r.data.data.order || {};
          if (!this.payPhone) {
            var ph = sessionStorage.getItem('userPhone') || '';
            this.payPhone = ph.replace('+254','').replace('254','');
          }
          this.initReleaseTimer();
          this.startStatusPoll();
          if (this.needsCompliance) this.loadComplianceDocs();
        } else {
          this.loadError = (r.data && r.data.message) || 'Order not found';
        }
      } catch(e) {
        console.error('[OrderDetail] load failed:', e);
        // axios timeout reports e.code === 'ECONNABORTED'
        this.loadError = (e && e.code === 'ECONNABORTED')
          ? 'The server took too long to respond. Tap Retry to try again.'
          : ((e && e.message) || 'Failed to load this order.');
      }
      this.loading = false;
    },

    // Client issue 35: without polling, a party with the page open never sees the other
    // side's status moves (e.g. seller still sees the inspection countdown after the
    // buyer already confirmed receipt). Silent refetch every 30s while the order is live.
    startStatusPoll(){
      if(this.statusPollTimer)return;
      var self=this;
      this.statusPollTimer=setInterval(async function(){
        var active=['paid','confirmed','preparing','dispatched','pending_seller_confirmation','in_transit','delivered','inspection'];
        if(!self.order||active.indexOf(self.order.status)<0){self.stopStatusPoll();return;}
        try{
          var f=new FormData();f.append('function','getOrder');f.append('orderId',self.$route.params.id);
          var r=await execute(f,flocker);
          if(r.data&&r.data.success){
            var fresh=r.data.data.order||{};
            if(fresh.status!==self.order.status||fresh.auto_release_at!==self.order.auto_release_at){
              self.order=fresh;
              self.initReleaseTimer();
            }
          }
        }catch(e){/* transient network error — next tick retries */}
      },30000);
    },
    stopStatusPoll(){if(this.statusPollTimer){clearInterval(this.statusPollTimer);this.statusPollTimer=null;}},

    // === AUTO-RELEASE COUNTDOWN ===
    initReleaseTimer(){
      this.stopReleaseTimer();
      var o=this.order;
      if((o.status==='delivered'||o.status==='inspection')&&o.auto_release_at){
        var releaseAt=new Date(o.auto_release_at.replace(/-/g,'/')).getTime();
        var now=Date.now();
        var diff=Math.floor((releaseAt-now)/1000);
        if(diff<=0){this.releaseSeconds=0;this.releaseExpired=true;return;}
        // Calculate total seconds (from delivery to auto_release)
        if(o.actual_delivery_at){
          var delivAt=new Date(o.actual_delivery_at.replace(/-/g,'/')).getTime();
          this.releaseTotalSecs=Math.max(1,Math.floor((releaseAt-delivAt)/1000));
        }else{this.releaseTotalSecs=172800;}
        this.releaseSeconds=diff;
        this.releaseExpired=false;
        var self=this;
        this.releaseTimer=setInterval(function(){
          self.releaseSeconds--;
          if(self.releaseSeconds<=0){self.releaseSeconds=0;self.releaseExpired=true;self.stopReleaseTimer();self.load();}
        },1000);
      }else{this.releaseSeconds=0;this.releaseExpired=false;}
    },
    stopReleaseTimer(){if(this.releaseTimer){clearInterval(this.releaseTimer);this.releaseTimer=null;}},

    // Dev-Requests #7 — installment toggle. Default to a sensible chunk of ~1/3 of
    // the outstanding balance when the buyer flips the toggle on.
    onToggleInstallment(){
      if (this.payAsInstallment) {
        this.payAmount = Math.max(1, Math.round(this.outstandingAmount / 3));
      }
    },

    // === PAYMENT (STK Push from Order Detail) ===
    async sendStkFromOrder(){
      if(!this.payPhone||this.payPhone.length<9){this.stkError='Enter a valid phone number';this.stkStatus='failed';return}
      this.stkSending=true;this.stkStatus='';this.stkError='';this.stkReceipt='';
      try{
        var f=new FormData();
        f.append('function','initiatePayment');
        f.append('orderId',this.order.id);
        f.append('userId',sessionStorage.getItem('userId'));
        f.append('paymentMethod','mpesa_stk');
        f.append('phone',this.payPhone);
        // Dev-Requests #7 — installment mode uses the buyer-entered amount (clamped
        // to outstanding). Full-pay mode pays the outstanding balance in one shot.
        var amt = this.payAsInstallment && this.payAmount > 0
          ? Math.min(Number(this.payAmount), this.outstandingAmount)
          : this.outstandingAmount;
        f.append('amount', amt);
        var r=await execute(f,flocker);
        if(r.data&&r.data.success){
          this.stkStatus='sent';
          this.stkRef=(r.data.data&&r.data.data.paymentRef)||'';
          this.stkCheckoutId=(r.data.data&&r.data.data.checkoutRequestId)||'';
          this.startStkPoll();
        }else{this.stkStatus='failed';this.stkError=(r.data&&r.data.message)||'STK push failed';}
      }catch(e){this.stkStatus='failed';this.stkError='Network error';}
      this.stkSending=false;
    },
    startStkPoll(){this.stopStkPoll();this.stkCountdown=5;var self=this;this.stkTimer=setInterval(function(){self.stkCountdown--;if(self.stkCountdown<=0){self.stkCountdown=5;self.checkStkStatus();}},1000);},
    stopStkPoll(){if(this.stkTimer){clearInterval(this.stkTimer);this.stkTimer=null;}},
    async checkStkStatus(){
      if(!this.stkRef&&!this.stkCheckoutId)return;
      try{
        var f=new FormData();f.append('function','checkPaymentStatus');
        if(this.stkRef)f.append('paymentRef',this.stkRef);
        if(this.stkCheckoutId)f.append('checkoutRequestId',this.stkCheckoutId);
        var r=await execute(f,flocker);
        if(r.data&&r.data.success&&r.data.data){
          var st=r.data.data.status;
          if(st==='completed'){this.stkStatus='completed';this.stkReceipt=r.data.data.mpesaReceipt||'';this.stopStkPoll();this.load();}
          else if(st==='failed'){this.stkStatus='failed';this.stkError=r.data.data.failureReason||'';this.stopStkPoll();}
          else if(st==='cancelled'){this.stkStatus='cancelled';this.stkError=r.data.data.failureReason||'';this.stopStkPoll();}
        }
      }catch(e){}
    },
    confirmAction(status,title,msg,type){this.confirmStatus=status;this.confirmTitle=title;this.confirmMsg=msg;this.confirmType=type||'success';this.showConfirm=true;this.actMsg='';this.etaInput='';this.etaError='';},
    raiseDispute(){var o=this.order;var other=this.isBuyer?(o.seller_name||''):(o.buyer_name||'');this.$router.push('/disputes?orderId='+o.id+'&orderNumber='+(o.order_number||'')+'&listingTitle='+encodeURIComponent(o.listing_title||'')+'&totalAmount='+(o.total_amount||'')+'&otherParty='+encodeURIComponent(other));},
    leaveReview(){var o=this.order;var sellerId=o.seller_id||'';var sellerName=o.seller_name||'';this.$router.push('/reviews?orderId='+o.id+'&orderNumber='+(o.order_number||'')+'&listingTitle='+encodeURIComponent(o.listing_title||'')+'&sellerId='+sellerId+'&sellerName='+encodeURIComponent(sellerName)+'&contextType=animal_order');},
    async executeConfirmed(){
      if(this.confirmStatus==='in_transit'){
        if(!this.etaInput){this.etaError='Please enter the estimated arrival time.';return;}
        this.etaError='';
      }
      this.actLoading=true;this.actMsg='';var wasComplete=this.confirmStatus==='completed';var amt=this.sellerPayout;var seller=this.order.seller_name;var orderNum=this.order.order_number;try{var f=new FormData();f.append('function','updateOrderStatus');f.append('orderId',this.order.id);f.append('status',this.confirmStatus);f.append('userId',sessionStorage.getItem('userId'));if(this.confirmStatus==='in_transit'&&this.etaInput)f.append('estimatedArrival',this.etaInput);var r=await execute(f,flocker);if(r.data&&r.data.success){this.actOk=true;this.actMsg='Updated!';this.showConfirm=false;if(wasComplete){this.releasedAmount=amt;this.releasedSeller=seller;this.releasedOrder=orderNum;this.showReleaseSuccess=true}this.load()}else{this.actOk=false;this.actMsg=(r.data&&r.data.message)||'Failed';this.showConfirm=false}}catch(e){this.actOk=false;this.actMsg='Error';this.showConfirm=false}this.actLoading=false;},
    async messageOther(){
      var userId=sessionStorage.getItem('userId');var toId=this.otherPartyId;if(!toId)return;
      try{
        // First check if conversation already exists for this order
        var p1=userId<toId?userId:toId;var p2=userId<toId?toId:userId;
        var check=await executeJson({function:'getConversations',userId:userId},flocker);
        var existing=null;
        if(check.data&&check.data.success){
          var convs=check.data.data.conversations||[];
          for(var i=0;i<convs.length;i++){
            var c=convs[i];
            if(c.context_type==='order'&&c.context_id===this.order.id){existing=c;break}
            if((c.participant_one_id===p1&&c.participant_two_id===p2)||(c.participant_one_id===p2&&c.participant_two_id===p1)){
              if(!c.context_type||c.context_type==='general'){existing=c;break}
            }
          }
        }
        if(existing){
          this.$router.push('/messages/'+existing.id);return;
        }
        // No existing conversation — create one with default message
        var otherName=this.isBuyer?(this.order.seller_name||'Seller'):(this.order.buyer_name||'Buyer');
        var listing=this.order.listing_title||this.order.title||'your listing';
        var defaultMsg='Hi '+otherName+', I\'m reaching out regarding Order #'+this.order.order_number+' for '+listing+'. ';
        if(this.isBuyer){defaultMsg+='I\'d like to discuss the delivery details.'}
        else{defaultMsg+='I\'m preparing your order and wanted to confirm the details.'}
        var f=new FormData();f.append('function','sendMessage');f.append('senderId',userId);f.append('receiverId',toId);
        f.append('content',defaultMsg);f.append('contextType','order');f.append('contextId',this.order.id);
        if(this.order.listing_id)f.append('listingId',this.order.listing_id);
        var r=await execute(f,flocker);
        if(r.data&&r.data.success&&r.data.data.conversationId){this.$router.push('/messages/'+r.data.data.conversationId)}
        else{this.$router.push('/messages')}
      }catch(e){this.$router.push('/messages')}
    }
  }
};
</script>